import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import slbApi from "../services/slbApi";
import { Loader2, CheckCircle } from "lucide-react";

/**
 * Auth Callback Page
 * Handles the OIDC callback, completes sign-in, and redirects to return_to
 */
export default function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("processing"); // processing, success, error
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get("code");
        const state = searchParams.get("state");
        const errorParam = searchParams.get("error");
        const errorDescription = searchParams.get("error_description");

        // Check for errors from the authorization server
        if (errorParam) {
          setError(errorDescription || errorParam);
          setStatus("error");
          return;
        }

        if (!code || !state) {
          setError("Missing authorization code or state");
          setStatus("error");
          return;
        }

        // Call the backend callback endpoint
        // The backend will exchange the code, set cookies, and redirect
        const callbackUrl = `/auth/callback?${searchParams.toString()}`;
        
        // Make the request - backend will return a redirect
        const response = await slbApi.get(callbackUrl, {
          maxRedirects: 0,
          validateStatus: (status) => status >= 200 && status < 400,
        });

        // If we get here without redirect, extract return_to from state
        const stateParts = state.split("|");
        const returnTo = stateParts[1] || "/";

        setStatus("success");
        
        // Short delay to show success state
        setTimeout(() => {
          navigate(returnTo, { replace: true });
        }, 1000);

      } catch (err) {
        // Check if it's a redirect response (302)
        if (err.response?.status === 302 || err.response?.status === 301) {
          const redirectUrl = err.response.headers?.location;
          if (redirectUrl) {
            window.location.href = redirectUrl;
            return;
          }
        }

        console.error("Auth callback failed:", err);
        setError(err.response?.data?.detail || err.message || "Authentication failed");
        setStatus("error");
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  if (status === "processing") {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-white mb-2">
            Finishing sign-in...
          </h1>
          <p className="text-zinc-400">Please wait while we complete your authentication.</p>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-white mb-2">
            Sign-in successful!
          </h1>
          <p className="text-zinc-400">Redirecting you now...</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center max-w-md p-6">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-500 text-2xl">!</span>
          </div>
          <h1 className="text-xl font-semibold text-white mb-2">
            Authentication Failed
          </h1>
          <p className="text-zinc-400 mb-6">{error}</p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => navigate("/station-logbook/sso/start")}
              className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate("/login")}
              className="px-6 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors"
            >
              Manual Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
